/**
 * Layout State Model — unified dashboard layout engine (react-grid-layout).
 *
 * @typedef {Object} LayoutCoordinates
 * @property {number} x       Grid column index (0-based)
 * @property {number} y       Grid row index (0-based)
 * @property {number} w       Width in grid columns
 * @property {number} h       Height in grid rows
 * @property {number} [minW]  Minimum width in columns
 * @property {number} [minH]  Minimum height in rows
 *
 * @typedef {Object} WidgetConfig
 * @property {string}  id                 Unique widget identifier
 * @property {string}  title              Header label
 * @property {string}  [category]         Catalog grouping (defaults "General")
 * @property {LayoutCoordinates} layout   Default lg-breakpoint coordinates
 * @property {number}  [minW]             Min width in columns (default 2)
 * @property {number}  [minH]             Min height in rows (default 2)
 * @property {boolean} [noPadding]        Body renders without inner padding
 * @property {() => import("react").ReactNode} render  Widget body factory
 *
 * @typedef {Object} DashboardRoleState
 * @property {Object<string, LayoutCoordinates[]>} layouts   Per-breakpoint layout arrays
 * @property {string[]} minimized          IDs currently collapsed to h:1
 * @property {string[]} hidden             hiddenWidgetIds (deleted from canvas)
 * @property {Object<string, Object<string, number>>} savedH Pre-minimize heights per breakpoint
 */

export const GRID_BREAKPOINTS = { lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 };
export const GRID_COLS = { lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 };
export const GRID_ROW_HEIGHT = 40;
export const GRID_MARGIN = [12, 12];
export const GRID_MAX_H = 24;

export function storageKeyFor(role, viewId) {
    return `user_dashboard_layout_${role}_${viewId}`;
}

/** Bottom edge of a layout — restored widgets append here to avoid overlap. */
export function nextY(layout) {
    return layout && layout.length ? Math.max(...layout.map((i) => i.y + i.h)) : 0;
}

function widgetById(widgets, id) {
    return widgets.find((w) => w.id === id);
}

function sanitizeItem(it, wdef, cols, isMin) {
    const minW = Math.min(wdef?.minW || 2, cols);
    const minH = isMin ? 1 : (wdef?.minH || 2);
    const width = Math.max(minW, Math.min(it.w || minW, cols));
    return {
        i: it.i,
        x: Math.max(0, Math.min(it.x || 0, cols - width)),
        y: Math.max(0, it.y || 0),
        w: width,
        h: isMin ? 1 : Math.min(Math.max(minH, it.h || minH), GRID_MAX_H),
        minW,
        minH,
        maxH: GRID_MAX_H,
        isResizable: !isMin,
    };
}

/** Directive 13: default layouts anchor at (0,0) top-left per breakpoint. */
export function buildDefaultLayouts(widgets) {
    const layouts = {};
    const ordered = [...widgets].sort(
        (a, b) => (a.layout.y - b.layout.y) || (a.layout.x - b.layout.x)
    );
    for (const bp of Object.keys(GRID_COLS)) {
        const cols = GRID_COLS[bp];
        if (bp === "lg") {
            layouts.lg = widgets.map((w) => ({
                i: w.id, ...w.layout,
                minW: Math.min(w.minW || 2, cols), minH: w.minH || 2, maxH: GRID_MAX_H,
            }));
        } else if (bp === "md") {
            const scale = cols / 12;
            layouts.md = widgets.map((w) => {
                const minW = Math.min(w.minW || 2, cols);
                const width = Math.max(minW, Math.min(Math.round(w.layout.w * scale) || 1, cols));
                return {
                    i: w.id,
                    x: Math.max(0, Math.min(Math.round(w.layout.x * scale), cols - width)),
                    y: w.layout.y,
                    w: width, h: w.layout.h,
                    minW, minH: w.minH || 2, maxH: GRID_MAX_H,
                };
            });
        } else {
            let y = 0;
            layouts[bp] = ordered.map((w) => {
                const item = { i: w.id, x: 0, y, w: cols, h: w.layout.h, minW: 1, minH: w.minH || 2, maxH: GRID_MAX_H };
                y += w.layout.h;
                return item;
            });
        }
    }
    return layouts;
}

/**
 * Reconcile persisted state against the current widget catalog:
 * removed widgets are pruned, new rollout widgets append at the bottom,
 * hidden/minimized sets are filtered to known IDs.
 * @returns {DashboardRoleState}
 */
export function reconcileState(raw, widgets) {
    const ids = new Set(widgets.map((w) => w.id));
    const hidden = (raw?.hidden_widget_ids || raw?.hidden || []).filter((id) => ids.has(id));
    const minimized = (raw?.minimized || []).filter((id) => ids.has(id) && !hidden.includes(id));
    const savedH = {};
    Object.entries(raw?.saved_h || raw?.savedH || {}).forEach(([k, v]) => {
        if (ids.has(k) && v && typeof v === "object") savedH[k] = v;
    });

    const defaults = buildDefaultLayouts(widgets);
    const layouts = {};
    for (const bp of Object.keys(GRID_COLS)) {
        const cols = GRID_COLS[bp];
        const stored = Array.isArray(raw?.layouts?.[bp]) ? raw.layouts[bp] : null;
        const base = (stored || []).filter((it) => it && ids.has(it.i) && !hidden.includes(it.i));
        const present = new Set(base.map((it) => it.i));
        const items = base.map((it) =>
            sanitizeItem(it, widgetById(widgets, it.i), cols, minimized.includes(it.i))
        );
        for (const d of defaults[bp]) {
            if (hidden.includes(d.i) || present.has(d.i)) continue;
            items.push(stored ? { ...d, x: 0, y: nextY(items) } : d);
        }
        layouts[bp] = items;
    }
    return { layouts, minimized, hidden, savedH };
}
